import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import "../../styles/penalty-recovery.css";
import { ingestSessionFromUrl, openCta, trackCTA, fetchCaseBySession } from "../../lib/penaltyRecoveryApi";

// Module-level one-shot guard: GA conversion + bridge breadcrumb fire exactly once
// per page load, even under React StrictMode's dev double-mount (prevents
// double-counting the lead conversion in GA/GTM).
let _prConversionFired = false;
let _prCasePollCancel = null;

/**
 * COVID Penalty Recovery — post-checkout THANK-YOU page (kgob.com/penalty-recovery/thank-you).
 *
 * app.kgob.com redirects here after a successful $295 checkout, round-tripping the
 * visitor's session: kgob.com/penalty-recovery/thank-you?session=<id>. This page:
 *   1. Adopts that session id (so the "Sign in" CTA reuses it).
 *   2. Fires the GA/GTM conversion event `pr_checkout_complete` — the lead-conversion
 *      goal marketers wire in GTM (parallel to the PPC `lp_thank_you_view`).
 *   3. Records a `checkout_complete` breadcrumb to OUR backend (reusing track-cta) so
 *      the shared bridge record flips status → app.kgob.com sees it in the same
 *      uniform format as every other source.
 *   4. Prompts the client to sign in to their account on app.kgob.com.
 *
 * noindex — this is a post-conversion page, never crawled/indexed.
 */
export default function PenaltyRecoveryThankYou() {
  const [caseNumber, setCaseNumber] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    // Capture the case number app.kgob.com appends (?case=KG-2026-NNNNN). Optional.
    let cn = null;
    try {
      cn = new URLSearchParams(window.location.search).get("case");
      if (cn) {
        cn = cn.trim();
        setCaseNumber(cn);
      }
    } catch (e) { /* optional — never block */ }
    const sessionId = ingestSessionFromUrl();

    // Fallback (Method 2): if app.kgob.com didn't append ?case=, resolve it server-side.
    // The case can lag the redirect under webhook/poll races, so retry briefly.
    if (!cn && sessionId) {
      let cancelled = false;
      const delays = [0, 3000, 6000, 10000, 15000, 20000]; // ~54s total
      (async () => {
        for (const d of delays) {
          if (cancelled) return;
          if (d) await new Promise((r) => setTimeout(r, d));
          const res = await fetchCaseBySession(sessionId);
          if (cancelled) return;
          if (res && res.found && res.case_number) {
            setCaseNumber(res.case_number);
            return;
          }
        }
      })();
      _prCasePollCancel = () => { cancelled = true; };
    }

    if (!_prConversionFired) {
      _prConversionFired = true;
      // 1) GA / GTM conversion event (lead tracking goal).
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "pr_checkout_complete",
          funnel: "covid_penalty_recovery",
          session_id: sessionId || null,
          case_number: cn || null,
        });
      } catch (e) {
        /* never block on analytics */
      }

      // 2) Flip the bridge record to checkout_complete (uniform-format breadcrumb).
      trackCTA("checkout_complete", "thank_you");
    }

    // 3) Defense-in-depth noindex (Helmet meta override is unreliable site-wide).
    let robots = document.querySelector('meta[name="robots"]');
    const prev = robots ? robots.getAttribute("content") : null;
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex,follow");
    return () => {
      if (_prCasePollCancel) { _prCasePollCancel(); _prCasePollCancel = null; }
      if (prev) robots.setAttribute("content", prev);
      else robots.remove();
    };
  }, []);

  const copyCase = () => {
    if (!caseNumber) return;
    try {
      navigator.clipboard.writeText(caseNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { /* clipboard may be blocked; number is visible regardless */ }
  };

  return (
    <div className="pr-page" data-testid="penalty-recovery-thank-you">
      <Helmet>
        <title>Payment received — COVID Penalty Recovery | KGOB CPAs &amp; Advisors</title>
        <meta name="robots" content="noindex,follow" />
        <link rel="canonical" href="https://kgob.com/penalty-recovery/thank-you" />
      </Helmet>

      <main style={{ background: "#FAF8F3", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 24px" }}>
        <div style={{ maxWidth: 640, width: "100%", textAlign: "center" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 14, fontSize: 13, fontWeight: 600, letterSpacing: "0.13em", color: "#B5993F", textTransform: "uppercase", marginBottom: 24 }}>
            <span style={{ width: 34, height: 1.5, background: "#B5993F" }} />Payment received<span style={{ width: 34, height: 1.5, background: "#B5993F" }} />
          </div>

          <span style={{ display: "inline-flex", width: 64, height: 64, borderRadius: "50%", background: "rgba(0,164,191,0.12)", alignItems: "center", justifyContent: "center", marginBottom: 24 }}>
            <span style={{ color: "#00A4BF", fontSize: 32, fontWeight: 700 }}>✓</span>
          </span>

          <h1 style={{ fontFamily: "'Playfair Display',Georgia,serif", fontWeight: 500, fontSize: 42, lineHeight: 1.12, color: "#233853", marginBottom: 16, textWrap: "balance" }}>
            Thank you — your review is on its way.
          </h1>

          <p style={{ fontSize: 17.5, color: "#5C6776", lineHeight: 1.6, maxWidth: 500, margin: "0 auto 36px" }}>
            We've received your $295 payment for the COVID Penalty Recovery review. A KGOB CPA
            will pull your IRS account and identify exactly what's recoverable. To upload your
            details and track progress, sign in to your account.
          </p>

          {caseNumber && (
            <div
              data-testid="pr-thankyou-case-block"
              style={{ background: "#fff", border: "1px solid #E6E0D2", borderRadius: 14, padding: "22px 24px", maxWidth: 460, margin: "0 auto 32px", boxShadow: "0 6px 22px rgba(35,56,83,0.06)" }}
            >
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#B5993F", marginBottom: 10 }}>
                Your case number
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, flexWrap: "wrap" }}>
                <span
                  data-testid="pr-thankyou-case-number"
                  style={{ fontFamily: "'IBM Plex Mono','Courier New',monospace", fontSize: 26, fontWeight: 700, letterSpacing: "0.02em", color: "#233853" }}
                >
                  {caseNumber}
                </span>
                <button
                  onClick={copyCase}
                  data-testid="pr-thankyou-copy-case"
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, background: copied ? "#00A4BF" : "#233853", color: "#fff", fontWeight: 600, fontSize: 13, padding: "8px 14px", borderRadius: 7, border: "none", cursor: "pointer", font: "inherit", transition: "background-color 0.2s" }}
                >
                  {copied ? "Copied ✓" : "Copy"}
                </button>
              </div>
              <p style={{ fontSize: 13.5, color: "#5C6776", lineHeight: 1.55, margin: "14px 0 0" }}>
                Save this — you'll use it together with your email to sign in to your secure client portal.
              </p>
            </div>
          )}

          <button
            onClick={() => openCta("login", "thank_you")}
            data-testid="pr-thankyou-login-cta"
            style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "linear-gradient(180deg,#C8AC43,#B08C24)", color: "#233853", fontWeight: 700, fontSize: 16, padding: "16px 30px", borderRadius: 8, border: "none", cursor: "pointer", font: "inherit", boxShadow: "0 4px 14px rgba(176,140,36,0.28)" }}
          >
            Sign in to your account <span>→</span>
          </button>

          <p style={{ marginTop: 28, fontSize: 14, color: "#9098A4" }}>
            A confirmation email with your receipt and next steps is on its way. Questions?{" "}
            <a data-testid="penalty-recovery-thank-you-a-3" href="tel:18445993355" style={{ color: "#233853", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 3 }}>1-844-599-3355</a>
          </p>

          <div style={{ marginTop: 40, paddingTop: 28, borderTop: "1px solid #EFEDE6", fontSize: 13.5, color: "#9098A4" }}>
            <a href="/penalty-recovery" data-testid="pr-thankyou-home-link" style={{ color: "#9098A4", textDecoration: "underline", textUnderlineOffset: 3, marginRight: 18 }}>Back to main page</a>
            <a data-testid="penalty-recovery-thank-you-a-2" href="/penalty-recovery/termsofservice" style={{ color: "#9098A4", textDecoration: "underline", textUnderlineOffset: 3, marginRight: 18 }}>Terms of Service</a>
            <a data-testid="penalty-recovery-thank-you-a-1" href="/penalty-recovery/refundanddisputepolicy" style={{ color: "#9098A4", textDecoration: "underline", textUnderlineOffset: 3 }}>Refund &amp; Dispute Policy</a>
          </div>
        </div>
      </main>
    </div>
  );
}
